import { Span, SpanStatusCode } from '@opentelemetry/api';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { Resource } from '@opentelemetry/resources';
import { SimpleSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { FetchError } from '@opentelemetry/instrumentation-fetch/build/src/types';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { fetchCsrf } from '@performio/api/functions/fetchCsrf';

export const serviceName = 'frontend-ui';

const releaseVersion =
  document.querySelector('meta[name="version"]')?.getAttribute('content') ||
  'unknown';
const resource = new Resource({
  'service.name': serviceName,
  'service.version': releaseVersion,
});
const provider = new WebTracerProvider({ resource });

const collectorUrl = '/api/proxy/telemetry';
const { token } = fetchCsrf(document);

// /api/proxy/telemetry is admin-chain-only (administrator authority + CSRF). Participant pages
// have neither, so every export would be denied, write a security-audit row and 302 to the admin
// login. The csrf-token meta tag only exists on admin pages — its absence keeps tracing dormant.
const telemetryExportPossible = token != null;

if (telemetryExportPossible) {
  const collector = new OTLPTraceExporter({
    url: `${collectorUrl}`,
    headers: {
      'X-CSRF-TOKEN': token,
    },
  });

  provider.addSpanProcessor(new SimpleSpanProcessor(collector));
}
provider.register({ contextManager: new ZoneContextManager() });

export const webTracerWithZone = provider.getTracer(serviceName);

let bindingSpan: Span | undefined;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(window as any).startBindingSpan = (
  traceId: string,
  spanId: string,
  traceFlags: number,
) => {
  bindingSpan = webTracerWithZone.startSpan('');
  bindingSpan.spanContext().traceId = traceId;
  bindingSpan.spanContext().spanId = spanId;
  bindingSpan.spanContext().traceFlags = traceFlags;
};

export function addGlobalSpanAttributes(
  span: Span,
  options: { includeUser?: boolean } = {},
) {
  const { includeUser = true } = options;
  span.setAttribute('path', window.location.pathname + window.location.search);
  span.setAttribute('host', window.location.host);
  span.setAttribute(
    'tenant_url',
    document
      .querySelector('meta[name="principalTenantIdentifier"]')
      ?.getAttribute('content') || 'unknown',
  );
  // user_name is PII — kept off the agent telemetry spans (chat/step/execute_tool/
  // invoke_agent, see AI-237). Callers that need it (e.g. the user_feedback span)
  // use the default includeUser = true.
  if (includeUser) {
    span.setAttribute(
      'user_name',
      document
        .querySelector('meta[name="principalUsername"]')
        ?.getAttribute('content') || 'unknown',
    );
  }
}

// Only instrument fetch when spans can actually be exported (see above).
if (telemetryExportPossible) {
  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        ignoreUrls: [
          // Anchored with escaped dots so unrelated hosts can't match.
          /^https:\/\/analytics\.google\.com\//,
          /^https:\/\/fast\.appcues\.com\//,
        ],
        propagateTraceHeaderCorsUrls: [new RegExp('https://*.performio.co/*')],
        clearTimingResources: true,
        applyCustomAttributesOnSpan: (
          span: Span,
          _request: Request | RequestInit,
          result: Response | FetchError,
        ) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const attributes = (span as any).attributes;
          if (attributes.component === 'fetch') {
            span.updateName(
              `${attributes['http.method']} ${attributes['http.url']}`,
            );
            addGlobalSpanAttributes(span);
          }
          if (result instanceof Error) {
            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: result.message,
            });
            span.recordException(result.stack || result.name);
          }
          if (result instanceof Response && !result.ok) {
            span.setStatus({
              code: SpanStatusCode.ERROR,
              message: result.statusText,
            });
          }
        },
      }),
    ],
  });
}

// Capture all console errors
window.addEventListener(
  'error',
  (event) => {
    if (!event.error) {
      // File loading errors, we may want to trace these at some point
      return;
    }

    const { message, filename, lineno, colno, error } = event;

    const eventError = {
      type: event.type,
      message: message ?? 'Unknown error',
      stack: error.stack ? error.stack : `${lineno}:${colno} ${filename}`,
    };

    webTracerWithZone.startActiveSpan('consoleError', (span) => {
      addGlobalSpanAttributes(span);
      span.setStatus({ code: SpanStatusCode.ERROR });
      span.recordException(eventError);
      span.end();
    });
  },
  {
    capture: true,
    once: false,
    passive: true,
  },
);

// Capture all unhandled promise rejections
window.addEventListener(
  'unhandledrejection',
  (event) => {
    const { reason } = event;

    webTracerWithZone.startActiveSpan('unhandledRejection', (span) => {
      addGlobalSpanAttributes(span);
      span.setStatus({ code: SpanStatusCode.ERROR });
      span.recordException({
        name: 'unhandledrejection',
        message: reason instanceof Error ? reason.message : String(reason),
        stack: reason instanceof Error ? reason.stack : undefined,
      });
      span.end();
    });
  },
  {
    passive: true,
  },
);

export function traceSpan<F extends (span: Span) => ReturnType<F>>(
  name: string,
  func: F,
): ReturnType<F> {
  return webTracerWithZone.startActiveSpan(name, (span: Span) => {
    addGlobalSpanAttributes(span);

    try {
      const result = func(span);

      if (result instanceof Promise) {
        return result
          .then((res) => {
            span.end();
            return res;
          })
          .catch((error) => {
            span.setStatus({ code: SpanStatusCode.ERROR });
            span.addEvent('error', {
              message: (error as Error)?.message ?? 'Unknown error',
              stack: (error as Error)?.stack ?? 'No stack trace',
            });
            span.end();
            throw error;
          }) as ReturnType<F>;
      }

      span.end();
      return result;
    } catch (error) {
      span.setStatus({ code: SpanStatusCode.ERROR });
      span.addEvent('error', {
        message: (error as Error)?.message ?? 'Unknown error',
        stack: (error as Error)?.stack ?? 'No stack trace',
      });
      span.end();
      throw error;
    }
  });
}
